// core/api.js — cliente único do backend Apps Script.
//
// Origem (monólito): linhas 3437-3441 e 3533-3558 do legacy/App_Eloquence_v18_FREQ_COMPLETA.html
// (constantes ALUNOS_CONFIG + função buscarAlunosDaAPI).
//
// Decisões travadas no MIGRATION.md:
//  - Deploy consolidado: AKfycby51JYxGjBnFxGfEK9GLc_2bS4Pm... (?action=alunos|frequencia|resumo|turma)
//  - Deploy deprecado (?action=all) NÃO é usado — ficará removido na Fase 6.
//  - Formato de resposta mantido: { success, timestamp, data }.
//  - Cache de alunos com TTL de 4 dias continua em Storage (Fase 3); aqui só HTTP.
//  - POST vai chegar na Fase 7 (registrar frequência admin). Stub preparado.
//
// Uso típico:
//   import { api } from './core/api.js';
//   const alunos = await api.get('alunos');          // retorna result.data já desembrulhado
//   const freq   = await api.get('frequencia');
//   const prog  = await api.getProgramacao();        // v19 Fase 4, com cache de 24h
//   await api.post('registrar', { cpf, data, ... }); // Fase 7

import { storage } from './storage.js';

const CONFIG = {
  BASE_URL: 'https://script.google.com/macros/s/AKfycby51JYxGjBnFxGfEK9GLc_2bS4PmgdPTosGkIFTZSwISi835EspBcyOcmuvV0gyiZZQFw/exec',
  DEFAULT_TIMEOUT_MS: 7000,
};

// Cache em memória (por aba aberta). Cache persistente com TTL vai em storage.js.
const memCache = new Map();

/**
 * Monta URL com querystring + anti-cache `t`.
 */
function buildUrl(action, params = {}) {
  const qs = new URLSearchParams({ action, t: String(Date.now()), ...params });
  return `${CONFIG.BASE_URL}?${qs.toString()}`;
}

/**
 * GET com timeout via AbortController. Desembrulha `{success, data}`.
 *
 * @param {string} action - valores atuais: 'alunos' | 'frequencia' | 'resumo' | 'turma'
 * @param {object} [options]
 * @param {object} [options.params]  querystring extra (ex.: { turma: '14' })
 * @param {number} [options.timeoutMs]
 * @param {boolean} [options.useMemCache]  se true, reusa resposta da aba corrente
 * @returns {Promise<any>} resultado desembrulhado (result.data)
 */
async function get(action, { params = {}, timeoutMs = CONFIG.DEFAULT_TIMEOUT_MS, useMemCache = false } = {}) {
  const cacheKey = `${action}:${JSON.stringify(params)}`;
  if (useMemCache && memCache.has(cacheKey)) {
    return memCache.get(cacheKey);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(buildUrl(action, params), {
      method: 'GET',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}`, { status: response.status, action });
    }

    const result = await response.json();
    if (!result.success || result.data === undefined) {
      throw new ApiError('Resposta inválida da API', { result, action });
    }

    if (useMemCache) memCache.set(cacheKey, result.data);
    return result.data;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new ApiError(`Timeout após ${timeoutMs}ms`, { action, cause: 'timeout' });
    }
    if (err instanceof ApiError) throw err;
    throw new ApiError(err.message || 'Erro de rede', { action, cause: err });
  }
}

/**
 * POST para a API do Apps Script. Ativado na Fase 7.
 *
 * Ações suportadas hoje:
 *   - 'registrar': grava lote de frequência na aba FREQUENCIA_ADMIN.
 *
 * Usa timeout maior que o GET porque gravação em Sheets pode levar >7s
 * quando a planilha está sob carga (ex.: trigger do auditor rodando).
 */
async function post(action, body = {}, { timeoutMs = 15000 } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // Apps Script webapps não suportam JSON preflight facilmente;
    // o POST vai usar 'text/plain' + body serializado (padrão Apps Script).
    const response = await fetch(buildUrl(action), {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(body),
    });
    clearTimeout(timeoutId);

    if (!response.ok) throw new ApiError(`HTTP ${response.status}`, { status: response.status, action });

    const result = await response.json();
    if (!result.success) {
      throw new ApiError(result.error || 'Falha no POST', { result, action });
    }
    return result.data ?? result;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new ApiError(`Timeout após ${timeoutMs}ms`, { action, cause: 'timeout' });
    if (err instanceof ApiError) throw err;
    throw new ApiError(err.message || 'Erro de rede', { action, cause: err });
  }
}

/**
 * Limpa cache em memória (útil após POSTs que invalidam dados).
 */
function invalidate(action) {
  if (!action) {
    memCache.clear();
    return;
  }
  for (const key of memCache.keys()) {
    if (key.startsWith(`${action}:`)) memCache.delete(key);
  }
}

// ========== v19 Fase 4: programação com cache persistente 24h ==========

const PROGRAMACAO_CACHE_KEY = 'programacao';
const PROGRAMACAO_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Busca a programação de aulas (aba PROGRAMAÇÃO 2026 no Sheets) com
 * cache persistente de 24h em localStorage.
 *
 * Formato de cada aula (definido pelo backend em getProgramacaoData):
 *   {
 *     ano: 2026,
 *     data: "2026-01-23",       // ISO YYYY-MM-DD
 *     dia_semana: "sexta",
 *     turno: "noite" | "manha" | "tarde",
 *     professor: "Yang Mendes",
 *     materia: "Comunicação de Alto Impacto",
 *     turmas: [34, 31, 32]
 *   }
 *
 * @param {object} [opts]
 * @param {boolean} [opts.forceRefresh=false] ignora cache e busca de novo
 * @param {number}  [opts.timeoutMs]          timeout de rede
 * @returns {Promise<Array<object>>}
 */
async function getProgramacao({ forceRefresh = false, timeoutMs } = {}) {
  if (!forceRefresh) {
    const cached = storage.getWithTTL(PROGRAMACAO_CACHE_KEY, null);
    if (cached !== null && Array.isArray(cached)) {
      return cached;
    }
  }

  const dados = await get('programacao', { timeoutMs });
  if (Array.isArray(dados)) {
    storage.setWithTTL(PROGRAMACAO_CACHE_KEY, dados, PROGRAMACAO_TTL_MS);
  }
  return dados;
}

/**
 * Força invalidação do cache de programação (próxima chamada refaz a rede).
 * Chamar quando o admin editar a aba — não há esse fluxo ainda, mas o hook
 * está pronto.
 */
function invalidateProgramacao() {
  storage.remove(PROGRAMACAO_CACHE_KEY);
}

/**
 * Erro customizado para diferenciar falha de rede vs. validação.
 */
export class ApiError extends Error {
  constructor(message, meta = {}) {
    super(message);
    this.name = 'ApiError';
    this.meta = meta;
  }
}

// ========== v19.1 Hardening Ponto 3 (H4): consentimento LGPD ==========

/**
 * Registra o consentimento granular do titular no backend (aba CONSENTIMENTOS_LGPD).
 * Append-only: cada aceite ou revogação gera uma nova linha.
 *
 * Best-effort: se a chamada falhar (rede, backend não deployado), a função
 * retorna {ok:false, error} e o caller (consent-modal) mantém snapshot local.
 * O modal NÃO bloqueia login por falha de rede — a UX do usuário não pode
 * depender da latência do Apps Script.
 *
 * @param {object} payload
 * @param {string} payload.idToken  JWT Google (obrigatório)
 * @param {string} payload.version  Versão do Termo aceita
 * @param {object} payload.choices  Finalidades (execucao_curso, marketing, etc)
 * @returns {Promise<{ok:true, data}|{ok:false, error:string}>}
 */
async function registrarConsentimento(payload) {
  return _postConsentimento('registrarConsentimento', payload);
}

/**
 * Revoga consentimento (mesmo endpoint, evento diferente).
 * Registra uma nova linha na aba com evento='REVOGAR' e o novo estado das finalidades.
 */
async function revogarConsentimento(payload) {
  return _postConsentimento('revogarConsentimento', payload);
}

/**
 * H6 — Exportação de dados do titular (Art. 18 II LGPD).
 * Retorna snapshot completo em JSON. Timeout maior porque o backend lê
 * várias abas para compor o payload.
 *
 * @param {object} payload
 * @param {string} payload.idToken JWT Google obrigatório
 * @param {string} [payload.uaHash] hash curto do user-agent (auditoria)
 * @returns {Promise<{ok:true, data}|{ok:false, error:string}>}
 */
async function exportarMeusDados({ idToken, uaHash = '' } = {}) {
  if (!idToken) return { ok: false, error: 'idToken obrigatório' };
  try {
    const data = await get('exportarMeusDados', {
      params: { idToken, ua: uaHash },
      timeoutMs: 15000,
    });
    return { ok: true, data };
  } catch (err) {
    console.warn('[api] exportarMeusDados falhou:', err?.message || err);
    return { ok: false, error: err?.message || 'erro desconhecido' };
  }
}

async function _postConsentimento(action, payload) {
  try {
    const data = await post(action, payload, { timeoutMs: 5000 });
    return { ok: true, data };
  } catch (err) {
    console.warn(`[api] ${action} falhou:`, err?.message || err);
    return { ok: false, error: err?.message || 'Falha de rede' };
  }
}

// ========== v19.2 R5: refresh manual (botão "Atualizar" no admin) ==========

/**
 * Limpa todos os caches do cliente (programação, alunos, frequência, memória).
 * Usado pelo botão "Atualizar" do admin para forçar refetch do backend.
 * NÃO afeta sessão de login nem dados do backend — apenas caches locais.
 */
function forceRefreshAll() {
  try {
    memCache.clear();
  } catch (_) {}
  try {
    storage.remove(PROGRAMACAO_CACHE_KEY);
  } catch (_) {}
  try {
    storage.remove('alunos_cache');
  } catch (_) {}
  try {
    storage.remove('frequencia');
  } catch (_) {}
  console.info('[api] forceRefreshAll: caches limpos');
}

export const api = {
  get,
  post,
  invalidate,
  getProgramacao,
  invalidateProgramacao,
  registrarConsentimento,
  revogarConsentimento,
  exportarMeusDados,
  forceRefreshAll,
  _config: CONFIG,
};
